import type { ToastIntent } from "@lib/brazeToaster";

export const loadingIndicatorSelector = ".bcl-progress-container, .spinner-container";

/**
 * Content Operator never reads: content explicitly withheld from it, the Operator debug modal,
 * the page footer every page carries, and the screen-reader text appended to external links.
 *
 * Shared by everything that reports page content to Operator, so a subtree withheld from the
 * scraper cannot reach Operator by another route.
 */
export const notReadableByOperatorSelector = [
  ".blocked-in-agentic-context",
  ".db-operator-debug",
  ".db-page-footer",
  "#open-in-new-tab",
].join(", ");

/** Drag-and-drop composer roots whose chrome scrape is forwarded to Operator. */
export const dragAndDropEditorRootSelector =
  ".db-drag-and-drop-composer, .db-banner-drag-and-drop-composer, .line-drag-and-drop-editor";

/**
 * Portaled, non-modal content Operator should read: a presence-only opt-in attribute.
 *
 * Beacon renders poppers (popovers, menus, tooltips, date pickers) through a Portal appended to
 * `document.body`, outside the scraped `#main-container` / `#main-row`, and the modal detection in
 * `findVisibleModals` deliberately ignores portals so those transient poppers are not reported as
 * modals. That leaves a popover *panel* that is genuinely part of the page's controls invisible to
 * Operator, so such a panel opts in by carrying `data-operator-overlay`. The panel's own contents
 * describe it; the attribute's value is never read.
 */
export const operatorOverlaySelector = "[data-operator-overlay]";

/** PII withheld from Operator on US-02 (HIPAA), matching what is blocked from FullStory. */
export const blockedInFullstorySelector = ".blocked-in-fullstory";

/** Appended to an editor read whose controls have not finished loading. */
export const editorDocumentLoadingNote = "(Some editor content is still loading; this read refreshes on its own.)";

// How long to wait before retrying the scrape
export const scrapeRetryInitialDelayMs = 250;
// Maximum delay we'll wait between scrapes
export const scrapeRetryMaxDelayMs = 1000;
// How long to wait total before giving up and returning the latest content
export const scrapeRetryMaxWaitMs = 5000;

/**
 * Developer context message when the page is still loading. Unlike the `read_current_page`
 * tool, the user-message context path cannot wait asynchronously, so Operator is directed to
 * call the tool, which retries until loading clears.
 */
export const pageStillLoadingUseReadCurrentPageMessage =
  "The current page is still loading (loading indicators are visible on the page). Automatic page content could not be provided. Call the `read_current_page` tool before responding so the page can finish loading and you receive the up-to-date page content.";

/** Tags the scraper keeps in its output. Every other element is a transparent layout wrapper. */
export const keptTags = new Set([
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "a",
  "button",
  "input",
  "textarea",
  "select",
  "option",
  "label",
  "fieldset",
  "legend",
  "ul",
  "ol",
  "li",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
  "pre",
  "img",
  "iframe",
]);

/**
 * Computed `display` values that do not start a new line. jsdom reports "" for elements its default
 * stylesheet does not cover, where a browser reports "inline".
 */
export const inlineDisplays = new Set([
  "",
  "inline",
  "inline-block",
  "inline-flex",
  "inline-grid",
  "inline-table",
  "table-cell",
  "contents",
]);

/**
 * CSS class selectors for modal content elements. These cover Beacon modals and custom
 * full-screen modal overlays that bypass role="dialog" / .bcl-modal-content.
 */
export const modalClassSelectors = [
  ".bcl-modal-content", // Beacon's modal content container (most common case)
  ".db-dashboard-drawer .bcl-drawer-content", // DashboardDrawer content (e.g. table row detail overlays)
  ".db-modal-edit-step", // Canvas step editor modals
  ".db-drag-and-drop-composer", // DnD composer modals (email, line, landing page)
  ".db-banner-drag-and-drop-composer", // Banner DnD composer modal
  ".db-banner-html-composer-portal", // Banner HTML full-screen composer portal
  ".db-email-message-composer-portal", // Email message composer portal
  ".db-content-block-composer-portal", // Content Block full-screen composer (portaled outside #main-container)
  ".db-preview-and-heatmap-modal", // Email preview & heatmap modal
  ".db-inbox-vision-preview-modal", // Inbox Vision preview modal
  ".workflow-modal", // Workflow modal
  ".modal", // Modal backdrop
];

/** Toastify renders each toast wrapper with one of these modifier classes. */
export const toastifyIntentClassToLabel: Record<string, ToastIntent> = {
  "Toastify__toast--error": "danger",
  "Toastify__toast--warning": "warning",
  "Toastify__toast--info": "info",
  "Toastify__toast--success": "success",
};

export const pageLead = "Current page seen by the user:";

/**
 * Leads the scrape only while a modal is open, which makes it a stronger signal than a sentence
 * that is always there. It names the tags it refers to.
 */
export const modalOpenLead =
  "Current page seen by the user. IMPORTANT: a modal is open on top of the page. The user is viewing the <dialog open> content, not the <main inert> page behind it. Prioritize the modal.";

/**
 * Explains the page format once, in the `read_current_page` tool description, so the scrape
 * itself carries no prose beyond {@link modalOpenLead}.
 */
export const pageContentFormatNotes =
  "Page content is HTML stripped to what the user sees. <dialog open> is a modal the user is looking at and <main inert> the page behind it. <overlay> is a panel open on top of the page. <toast> is a notification; `recent` means it already closed. <iframe> contents are visible to the user but not to you. `data-operator-redacted` marks a value withheld from you. Element ids are the tool entity ids.";
